import {
  FLOW_EXPIRED_MESSAGE,
  getBifrostMessage,
} from '@joinrepublic/bifrost-messages';
import {
  ContinueWith,
  ErrorGeneric,
  LoginFlow,
  SuccessfulNativeLogin,
  UiText,
  UpdateLoginFlowBody,
} from '@ory/kratos-client';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse, isAxiosError } from 'axios';

import { useGlobalAuthApi } from '@r-client/shared/data/auth';

import {
  flowIsExpired,
  getContinueWithNode,
  getFlowInputNode,
} from '../../utils/shared/flow-utils';

export interface ILoginFlowError {
  field: 'identifier' | 'password' | 'root';
  message: string;
  id?: number;
}

export type LoginMutationResult =
  | {
      success: true;
      redirectTo?: string;
      globalLogin?: SuccessfulNativeLogin;
      proxyResponseHeaders?: Record<string, string>;
    }
  | {
      success: false;
      requiresVerification: true;
      email: string;
      verificationFlowId: string;
      errors?: ILoginFlowError[];
    }
  | {
      success: false;
      requiresVerification?: false;
      flow: LoginFlow;
      errors: ILoginFlowError[];
    };

// Preserve discriminated union when omitting fields
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

export type ILoginMutationVariables = DistributiveOmit<
  UpdateLoginFlowBody,
  'transient_payload' | 'csrf_token'
> & {
  flow?: LoginFlow;
  transient_payload?: {
    captcha_token: string | null;
  };
};

/**
 * Extracts error messages from Ory flow UI nodes and returns them as structured errors
 */
function extractFlowErrors(lf: LoginFlow): ILoginFlowError[] {
  const errors: ILoginFlowError[] = [];

  const addErrors = (messages: UiText[], field?: ILoginFlowError['field']) => {
    if (messages.length) {
      errors.push(
        ...messages.map((message) => ({
          field: field || 'root',
          message: getBifrostMessage(message),
          id: message.id,
        }))
      );
    }
  };

  const identifierInput = getFlowInputNode(lf.ui.nodes, 'identifier');
  const passwordInput = getFlowInputNode(lf.ui.nodes, 'password');

  addErrors(lf?.ui.messages || []);
  addErrors(identifierInput?.messages || [], 'identifier');
  addErrors(passwordInput?.messages || [], 'password');

  return errors;
}

export const useSubmitLoginMutation = () => {
  const globalAuthApi = useGlobalAuthApi();

  const mutation = useMutation({
    meta: {
      reportingKey: ['global-auth', 'login-flow', 'submit'],
      reportLevel: 'info',
    },
    mutationFn: async ({
      flow,
      ...payload
    }: ILoginMutationVariables): Promise<LoginMutationResult> => {
      if (!payload?.transient_payload?.captcha_token) {
        throw new Error(
          'Failed to verify your request, please complete the captcha and try again.'
        );
      }

      if (!flow?.ui.action)
        throw new Error('Login flow not found, please try again.');

      if (flowIsExpired(flow)) {
        throw new Error(FLOW_EXPIRED_MESSAGE);
      }

      const csrfToken = getFlowInputNode(flow.ui.nodes, 'csrf_token')
        ?.attributes?.value;

      let response: AxiosResponse<
        SuccessfulNativeLogin | LoginFlow | ErrorGeneric
      >;
      let error: Error | null = null;

      try {
        response = await globalAuthApi.updateLoginFlow({
          flow: flow.id,
          updateLoginFlowBody: {
            ...payload,
            csrf_token: csrfToken || '',
          } as UpdateLoginFlowBody,
        });
      } catch (err) {
        if (isAxiosError(err) && err.response) {
          response = err.response;
          error = err;
        } else {
          throw new Error('Something went wrong, please try again later.');
        }
      }

      if (error && response.data && 'ui' in response.data) {
        const flowErrors = extractFlowErrors(response.data);
        return { success: false, flow: response.data, errors: flowErrors };
      }

      if ('error' in response.data) {
        const { error: dataError } = response.data as ErrorGeneric;
        if (dataError.id === 'session_verified_address_required') {
          const continueWith = dataError.details as {
            continue_with?: ContinueWith[];
          };
          const showVerificationNode = getContinueWithNode(
            continueWith?.continue_with,
            'show_verification_ui'
          );
          if (showVerificationNode?.flow?.id) {
            return {
              success: false,
              requiresVerification: true,
              email: showVerificationNode.flow.verifiable_address,
              verificationFlowId: showVerificationNode.flow.id,
            };
          }
        }

        if (dataError.id === 'browser_location_change_required') {
          const target = (response.data as { redirect_browser_to?: string })
            .redirect_browser_to;
          if (target) {
            return {
              success: true,
              redirectTo: target,
            };
          }
        }

        return {
          success: false,
          flow,
          errors: [
            { field: 'root', message: dataError.reason || 'Login failed' },
          ],
        };
      }

      if (
        !response?.data ||
        !('session' in response.data) ||
        !response.data.session.active
      ) {
        throw new Error('Failed to login, please try again later.');
      }

      const redirectNode = getContinueWithNode(
        response.data.continue_with,
        'redirect_browser_to'
      );

      const rawHeaders: Record<string, unknown> =
        typeof (response.headers as { toJSON?: () => Record<string, unknown> })
          .toJSON === 'function'
          ? (
              response.headers as { toJSON: () => Record<string, unknown> }
            ).toJSON()
          : (response.headers as Record<string, unknown>);
      const proxyResponseHeaders = Object.fromEntries(
        Object.entries(rawHeaders)
          .filter(([k]) => {
            const lk = k.toLowerCase();
            return lk.startsWith('x-bst') || lk === 'via';
          })
          .map(([k, v]) => [k.toLowerCase(), String(v)])
      );

      return {
        success: true,
        redirectTo: redirectNode?.redirect_browser_to,
        globalLogin: response.data,
        proxyResponseHeaders,
      };
    },
  });

  return mutation;
};
